<template>
  <v-container
    fluid
    tag="section"
  >
    <v-card>
      <v-card-title>
        <span class="text-h5">Создание визита</span>
      </v-card-title>
      <v-card-text>
        <template v-if="!result">
          <v-row dense>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.title"
                label="Заголовок *"
                counter="256"
                maxlength="256"
                outlined
                dense
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-autocomplete
                v-model="form.from_place_id"
                :items="fromPlaces"
                label="Место-отправитель *"
                outlined
                dense
                no-data-text="Нет мест"
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-autocomplete
                v-model="form.to_place_id"
                :items="toPlaces"
                label="Место-приёмник *"
                outlined
                dense
                no-data-text="Нет мест"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <skud-date-field
                v-model="form.starts_at"
                label="Начало окна *"
                with-time
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <skud-date-field
                v-model="form.ends_at"
                label="Конец окна *"
                with-time
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.purpose"
                label="Цель"
                counter="2000"
                maxlength="2000"
                outlined
                dense
                rows="2"
                auto-grow
              />
            </v-col>
            <v-col cols="12">
              <skud-point-select
                v-model="form.point_ids"
                :place-id="form.to_place_id"
                label="Точки прохода *"
              />
            </v-col>
          </v-row>

          <div class="text-subtitle-1 mt-2 mb-2">
            Участники (1..500)
          </div>
          <v-data-table
            :headers="participant_headers"
            :items="form.participants"
            dense
            no-data-text="Нет участников"
            hide-default-footer
          >
            <template v-slot:item.subject_ref="{item}">
              <v-text-field
                v-model="item.subject_ref"
                dense
                hide-details
                maxlength="64"
              />
            </template>
            <template v-slot:item.group_ref="{item}">
              <v-text-field
                v-model="item.group_ref"
                dense
                hide-details
              />
            </template>
            <template v-slot:item.role_code="{item}">
              <v-text-field
                v-model="item.role_code"
                dense
                hide-details
              />
            </template>
            <template v-slot:item.actions="{index}">
              <v-btn
                icon
                small
                @click="form.participants.splice(index, 1)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <v-btn
            color="secondary"
            outlined
            small
            class="mt-2"
            @click="addParticipant"
          >
            <v-icon small>
              mdi-plus
            </v-icon>
            Добавить участника
          </v-btn>

          <v-row
            dense
            class="mt-4"
          >
            <v-col
              cols="12"
              md="8"
            >
              <v-textarea
                v-model="bulk"
                label="Вставить списком"
                hint="Построчно: subject_ref;group_ref;role_code (group_ref и role_code необязательны)"
                persistent-hint
                outlined
                dense
                rows="4"
              />
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-btn
                color="secondary"
                outlined
                @click="parseBulk"
              >
                Разобрать
              </v-btn>
            </v-col>
          </v-row>

          <v-checkbox
            v-model="form.submit"
            label="Сразу отправить на подтверждение"
            dense
          />

          <v-btn
            color="primary"
            :loading="loading"
            @click="create"
          >
            Создать
          </v-btn>
          <v-btn
            outlined
            class="ml-2"
            :disabled="loading"
            @click="$router.push($route.path.replace(/create$/, 'list'))"
          >
            Отмена
          </v-btn>
        </template>

        <template v-else>
          <v-alert
            type="success"
            outlined
          >
            Визит создан. Участников: {{ result.participants_count }}
          </v-alert>
          <v-alert
            v-for="(w, i) in result.warnings || []"
            :key="'warn_' + i"
            type="warning"
            outlined
            dense
          >
            {{ w }}
          </v-alert>
          <div class="text-subtitle-1 mt-2 mb-2">
            Карты участников
          </div>
          <div>
            <v-chip
              v-for="c in result.credentials || []"
              :key="c.subject_ref"
              small
              dark
              class="ma-1"
              :color="c.state === 'resolved' ? 'green' : 'red'"
            >
              {{ c.subject_ref }}<span v-if="c.card_uid">&nbsp;({{ c.card_uid }})</span>
            </v-chip>
          </div>
          <div class="mt-4">
            <v-btn
              color="primary"
              class="mr-2"
              @click="$router.push({ path: $route.path.replace(/create$/, 'view'), query: { id: result.visit_id, place: form.from_place_id } })"
            >
              Открыть визит
            </v-btn>
            <v-btn
              color="secondary"
              outlined
              @click="$router.push($route.path.replace(/create$/, 'list'))"
            >
              К списку
            </v-btn>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
// Страница создания визита. request_id — идемпотентный ключ (crypto.randomUUID).
import moment from 'moment'
import { SkudVisitService } from '@/views/skud/shared/services/skud-visit-service'
import { loadPlaces } from '@/views/skud/shared/services/skud-places-service'
import SkudPointSelect from '@/views/skud/components/SkudPointSelect.vue'
import SkudDateField from '@/views/skud/components/SkudDateField.vue'

export default {
  name: 'SkudVisitForm',
  components: {
    SkudPointSelect,
    SkudDateField
  },
  data () {
    return {
      service: null,
      places: [],
      loading: false,
      result: null,
      bulk: '',
      form: {
        title: '',
        purpose: '',
        starts_at: '',
        ends_at: '',
        from_place_id: null,
        to_place_id: null,
        point_ids: [],
        participants: [],
        submit: false
      },
      participant_headers: [
        { value: 'subject_ref', text: 'Субъект *', sortable: false },
        { value: 'group_ref', text: 'Группа', sortable: false },
        { value: 'role_code', text: 'Роль', sortable: false },
        { value: 'actions', text: '', sortable: false, width: '60px' }
      ]
    }
  },
  computed: {
    fromPlaces () {
      return this.places.filter(a => a.value !== this.form.to_place_id)
    },
    toPlaces () {
      return this.places.filter(a => a.value !== this.form.from_place_id)
    }
  },
  mounted () {
    this.service = new SkudVisitService()
    loadPlaces().then(list => { this.places = list })
  },
  methods: {
    addParticipant () {
      this.form.participants.push({ subject_ref: '', group_ref: '', role_code: '' })
    },
    // Парсинг «subject_ref;group_ref;role_code» построчно, merge с дедупом по subject_ref.
    parseBulk () {
      const known = {}
      for (const p of this.form.participants) {
        if (p.subject_ref) known[p.subject_ref] = true
      }
      for (const line of this.bulk.split('\n')) {
        const parts = line.split(';').map(s => s.trim())
        if (!parts[0] || known[parts[0]]) continue
        known[parts[0]] = true
        this.form.participants.push({ subject_ref: parts[0], group_ref: parts[1] || '', role_code: parts[2] || '' })
      }
      this.bulk = ''
    },
    validate () {
      if (!this.form.title) return 'Заполните заголовок'
      if (!this.form.from_place_id) return 'Выберите место-отправитель'
      if (!this.form.to_place_id) return 'Выберите место-приёмник'
      if (!this.form.starts_at || !this.form.ends_at) return 'Заполните период визита'
      if (moment(this.form.ends_at).isSameOrBefore(moment(this.form.starts_at))) return 'Конец окна должен быть позже начала'
      if (this.form.point_ids.length < 1) return 'Укажите хотя бы одну точку прохода'
      const participants = this.form.participants.filter(p => p.subject_ref)
      if (participants.length < 1 || participants.length > 500) return 'Участников должно быть от 1 до 500'
      return null
    },
    async create () {
      const err = this.validate()
      if (err) {
        this.$store.commit('snackbar', err)
        return
      }
      const body = {
        request_id: crypto.randomUUID(),
        from_place_id: String(this.form.from_place_id),
        to_place_id: String(this.form.to_place_id),
        title: this.form.title,
        purpose: this.form.purpose || null,
        starts_at: moment(this.form.starts_at).toISOString(),
        ends_at: moment(this.form.ends_at).toISOString(),
        point_ids: this.form.point_ids,
        participants: this.form.participants
          .filter(p => p.subject_ref)
          .map(p => ({
            subject_ref: p.subject_ref,
            group_ref: p.group_ref || null,
            role_code: p.role_code || null
          })),
        submit: this.form.submit
      }
      this.loading = true
      const res = await this.service.createVisit(this.form.from_place_id, body)
      this.loading = false
      if (res.ok) {
        this.result = res.data
      } else {
        this.$store.commit('snackbar', res.error)
      }
    }
  }
}
</script>
